import { __awaiter, __generator } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { useIdentity } from '@office-start/identity-service';
import { logErrorEvent } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { getAppDefinitionForAdminAcquiredApps } from '@office-start/m365-apps-service';
import { logError } from '@office-start/telemetry-service';
import * as React from 'react';
import create from 'zustand';
import { getAdminAcquiredAppsUserData, setAdminAcquiredAppsUserData, } from '../utils/adminAcquiresAppsUserData';
import { getNewAdminAcquiredApps } from '../utils/getNewAdminAcquiredApps';
function getUserStorageOptions(componentContext) {
    var correlationId = componentContext.correlationId, getAccessToken = componentContext.getAccessToken, getIdentity = componentContext.getIdentity, _a = componentContext.inMockMode, inMockMode = _a === void 0 ? false : _a, _b = componentContext.jwtAuthEnabled, jwtAuthEnabled = _b === void 0 ? false : _b, _c = componentContext.officeHomeApiRoot, officeHomeApiRoot = _c === void 0 ? '' : _c, workload = componentContext.workload;
    return {
        correlationId: correlationId,
        getAccessToken: getAccessToken,
        getIdentity: getIdentity,
        inMockMode: inMockMode,
        jwtAuthEnabled: jwtAuthEnabled,
        officeHomeApiRoot: officeHomeApiRoot,
        workload: workload,
    };
}
var useAdminAcquiredAppsUserDataStore = create(function (set) { return ({
    adminAcquiredApps: undefined,
    setAdminAcquiredApps: function (adminAcquiredApps) {
        set({ adminAcquiredApps: adminAcquiredApps });
    },
}); });
export var useAdminAcquiredApps = function () {
    var _a = useAdminAcquiredAppsUserDataStore(), adminAcquiredApps = _a.adminAcquiredApps, setAdminAcquiredApps = _a.setAdminAcquiredApps;
    var featureGates = useFeatureGates().data;
    var context = useOfficeComponentContext().data;
    var identity = useIdentity().data;
    React.useEffect(function () {
        if (!(featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) || !identity || !context || adminAcquiredApps)
            return;
        var isSubscribed = true;
        var fetchData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var newApps, timeStamp, appDefinitions, userStorageOptions, userData;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        newApps = [];
                        if (!(!identity.isMsa &&
                            ((_a = featureGates.result.adminAcquiredAppsGates) === null || _a === void 0 ? void 0 : _a.adminAcquiredAppsEnabled) &&
                            !adminAcquiredApps)) return [3 /*break*/, 3];
                        timeStamp = void 0;
                        return [4 /*yield*/, getAppDefinitionForAdminAcquiredApps()];
                    case 1:
                        appDefinitions = _b.sent();
                        userStorageOptions = getUserStorageOptions(context);
                        return [4 /*yield*/, getAdminAcquiredAppsUserData(userStorageOptions)];
                    case 2:
                        userData = _b.sent();
                        if (userData === null || userData === void 0 ? void 0 : userData.lastDismissedTimeUtc) {
                            timeStamp = userData.lastDismissedTimeUtc;
                        }
                        newApps = getNewAdminAcquiredApps(appDefinitions, timeStamp);
                        _b.label = 3;
                    case 3:
                        if (isSubscribed) {
                            setAdminAcquiredApps(newApps);
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        fetchData().catch(function (error) {
            var _a;
            if (isSubscribed) {
                setAdminAcquiredApps([]);
            }
            var message = "Error reading admin acquired apps user data: ".concat(error);
            logErrorEvent(KnownInstrumentationEvents.AdminAcquiredAppsUserDataReadError, (_a = {},
                _a["Error" /* InstrumentationParam.Error */] = message,
                _a));
            // TODO: Remove above telemetry once OTEL migration complete
            logError({
                eventName: KnownInstrumentationEvents.AdminAcquiredAppsUserDataReadError,
                errorType: 'AdminAcquiredApps_UserData_Error',
                message: message,
            });
        });
        return function () {
            isSubscribed = false;
        };
    }, [context, featureGates, identity, setAdminAcquiredApps]);
    var resetAdminAcquiredAppsStore = React.useCallback(function () {
        setAdminAcquiredApps([]);
    }, [setAdminAcquiredApps]);
    var setDismissedTimestamp = React.useCallback(function (newTimeStamp, skipResetStore) { return __awaiter(void 0, void 0, void 0, function () {
        var userStorageOptions, newUserData;
        return __generator(this, function (_a) {
            if (context) {
                userStorageOptions = getUserStorageOptions(context);
                newUserData = {
                    lastDismissedTimeUtc: newTimeStamp,
                };
                if (!skipResetStore) {
                    resetAdminAcquiredAppsStore();
                }
                setAdminAcquiredAppsUserData(newUserData, userStorageOptions).catch(function (error) {
                    var _a;
                    var message = "Error writing admin acquired apps user data: ".concat(error);
                    logErrorEvent(KnownInstrumentationEvents.AdminAcquiredAppsUserDataWriteError, (_a = {},
                        _a["Error" /* InstrumentationParam.Error */] = message,
                        _a));
                    // TODO: Remove above telemetry once OTEL migration complete
                    logError({
                        eventName: KnownInstrumentationEvents.AdminAcquiredAppsUserDataWriteError,
                        errorType: 'AdminAcquiredApps_UserData_Error',
                        message: message,
                    });
                });
            }
            return [2 /*return*/];
        });
    }); }, [context, resetAdminAcquiredAppsStore]);
    return React.useMemo(function () { return ({
        adminAcquiredApps: adminAcquiredApps,
        setDismissedTimestamp: setDismissedTimestamp,
        resetAdminAcquiredAppsStore: resetAdminAcquiredAppsStore,
    }); }, [adminAcquiredApps, setDismissedTimestamp, resetAdminAcquiredAppsStore]);
};
//# sourceMappingURL=useAdminAcquiredApps.js.map